import { useEffect, useState, useCallback } from "react";
import { Sparkles, Copy, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi } from "../../lib/api";
import { inputCls } from "./shared";

const COHORT_COLORS = {
  abandoned_search: "border-l-amber-500",
  repeat_visitor: "border-l-blue-500",
  quiet_leads: "border-l-red-500",
};

export function ReachTab() {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // {member, cohort}
  const [channel, setChannel] = useState("whatsapp");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminApi().get("/admin/reach/cohorts")
      .then((r) => setCohorts(r.data.cohorts))
      .catch(() => toast.error("Failed to load cohorts"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (member, cohortId, ch) => {
    setBusy(true);
    setMessage("");
    try {
      const { data } = await adminApi().post("/admin/reach/generate", {
        name: member.name, cohort: cohortId, channel: ch, route: member.route || "",
      });
      setMessage(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const openDialog = (member, cohortId) => {
    setTarget({ member, cohortId });
    setChannel("whatsapp");
    generate(member, cohortId, "whatsapp");
  };

  const switchChannel = (ch) => {
    setChannel(ch);
    if (target) generate(target.member, target.cohortId, ch);
  };

  const copyMsg = () => {
    navigator.clipboard.writeText(message);
    toast.success("Copied to clipboard");
  };

  const waLink = () => {
    const digits = (target?.member?.phone || "").replace(/[^0-9]/g, "");
    if (channel !== "whatsapp" || digits.length < 8 || !message) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="space-y-6" data-testid="reach-tab">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-heading font-bold text-slate-900">Farevault Reach — AI win-back cohorts</p>
          <p className="text-xs text-slate-500 mt-1">
            Cohorts are built from your analytics and lead capture. Contactable members are visitors we can match to a lead (email/phone). Visitor matching starts collecting from today onwards.
          </p>
        </div>
        <button data-testid="reach-refresh-btn" onClick={load} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#970C10] transition-colors duration-200">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading && <p className="text-sm text-slate-400 py-10 text-center">Building cohorts…</p>}

      {!loading && cohorts.map((c) => (
        <div key={c.id} className={`bg-white border border-slate-200 border-l-4 ${COHORT_COLORS[c.id]} rounded-lg overflow-hidden`} data-testid={`reach-cohort-${c.id}`}>
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <p className="font-heading font-bold text-slate-900">{c.label}</p>
              <p className="text-xs text-slate-500">{c.description}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900" data-testid={`reach-count-${c.id}`}>{c.total}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">{c.contactable.length} contactable</p>
            </div>
          </div>
          {c.contactable.length > 0 && (
            <table className="w-full text-sm border-t border-slate-100">
              <tbody>
                {c.contactable.map((m, i) => (
                  <tr key={`${c.id}-${i}`} className="border-t border-slate-50" data-testid={`reach-member-${c.id}-${i}`}>
                    <td className="px-5 py-2.5">
                      <p className="font-semibold text-slate-900">{m.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{m.phone || m.email || "—"}</p>
                    </td>
                    <td className="px-5 py-2.5 text-xs text-slate-600">
                      {m.route ? <span className="font-mono bg-slate-50 px-1.5 py-0.5 rounded">{m.route}</span> : (m.lead_type || "")}
                    </td>
                    <td className="px-5 py-2.5 text-xs text-slate-400">{m.last_seen ? new Date(m.last_seen).toLocaleDateString() : ""}</td>
                    <td className="px-5 py-2.5 text-right">
                      <button
                        data-testid={`reach-generate-${c.id}-${i}`}
                        onClick={() => openDialog(m, c.id)}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#970C10] hover:text-[#7A0A0D] transition-colors duration-200"
                      >
                        <Sparkles className="w-3.5 h-3.5" /> AI Win-back
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {c.contactable.length === 0 && (
            <p className="px-5 pb-4 text-xs text-slate-400">No contactable members yet{c.total > 0 ? ` — ${c.total} anonymous visitors in this cohort` : ""}.</p>
          )}
        </div>
      ))}

      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="reach-message-dialog">
          <DialogHeader><DialogTitle className="font-heading">Win-back for {target?.member?.name}</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-3">
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
                {["whatsapp", "email"].map((ch) => (
                  <button
                    key={ch}
                    data-testid={`reach-channel-${ch}`}
                    onClick={() => switchChannel(ch)}
                    disabled={busy}
                    className={`px-3 py-1 rounded-md text-xs font-bold transition-colors duration-200 ${channel === ch ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                  >
                    {ch === "whatsapp" ? "WhatsApp" : "Email"}
                  </button>
                ))}
              </div>
              {busy ? (
                <p className="text-sm text-slate-400 py-16 text-center">Writing the message…</p>
              ) : (
                <textarea readOnly className={`${inputCls} h-52 text-sm leading-relaxed`} value={message} data-testid="reach-message-text" />
              )}
              <div className="flex gap-2">
                <button
                  data-testid="reach-copy-btn"
                  onClick={copyMsg}
                  disabled={!message || busy}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-40 text-white font-bold text-sm py-2.5 rounded-lg transition-colors duration-200"
                >
                  <Copy className="w-4 h-4" /> Copy message
                </button>
                {waLink() && (
                  <a
                    data-testid="reach-wa-send-btn"
                    href={waLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white font-bold text-sm py-2.5 rounded-lg transition-colors duration-200"
                  >
                    <ExternalLink className="w-4 h-4" /> Send on WhatsApp
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
